import React from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SEO from '../components/SEO';
import esFlag from '../assets/images/languages/es.svg';
import gbFlag from '../assets/images/languages/gb.svg';
import heroImage from '../assets/images/livre.webp';
import carteLangueImage from '../assets/images/carte_langue.webp';
import exerciceLangueImage from '../assets/images/exercice_langue.webp';
import ficheMemoImage from '../assets/images/fiche_memo.webp';
import lessonImage from '../assets/images/lesson.webp';
import quizzImage from '../assets/images/quizz.webp';

function Home() {
  const { interfaceLang } = useParams();
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL;

  //SEO
  const { pathname } = useLocation();
  const base = process.env.REACT_APP_SITE_URL; // e.g. https://exemple.com
  const canonical = `${base}${pathname}`;

  const alternates = [
    { hrefLang: 'fr', href: `${base}/fr` },
    { hrefLang: 'en', href: `${base}/en` },
    { hrefLang: 'x-default', href: `${base}/en` },
  ];

  const handleTestNotification = () => {
    const token = localStorage.getItem('access_token');

    axios.get(`${API_URL}/cours/api/test-notification/`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then((response) => {
      if (response.data.status === 'success') {
        console.log("Notification envoyée avec succès");
      } else {
        console.error("Erreur lors de l'envoi de la notification");
      }
    })
    .catch((error) => {
      console.error("Erreur lors de la requête :", error);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        lang={interfaceLang}
        title={interfaceLang === 'fr' ? "Apprends une langue facilement" : "Learn a language easily"}
        description={interfaceLang === 'fr'
          ? "Exercices interactifs, vocabulaire et cours pour progresser rapidement."
          : "Interactive exercises, vocabulary, and courses to improve fast."
        }
        canonical={canonical}
        image={`${base}/easylanguage_512.png`}
        alternates={alternates}
      />
      {/* HERO SECTION */}
      <section className="relative w-full h-[80vh] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="bg-black bg-opacity-30 p-8 text-center text-white w-full h-full flex flex-col justify-center">
          <h1 className="text-5xl font-bold mb-4 text-shadow">{t('welcomeMessage')}</h1>
          <p className="text-xl font-semibold text-shadow mb-6">{t('siteDescription')}</p>
          <div className="flex justify-center space-x-4">
            <Link to={`/${interfaceLang}/signup`} className="bg-green-500 px-6 py-3 rounded-xl text-white hover:bg-green-600 transition">{t('signup')}</Link>
            <Link to={`/${interfaceLang}/login`} className="bg-blue-500 px-6 py-3 rounded-xl text-white hover:bg-blue-600 transition">{t('login')}</Link>
          </div>
        </div>
      </section>

      {/* LANGUAGES SECTION */}
      <section className="py-16 bg-white text-center">
        <h2 className="text-3xl font-bold mb-8">{t('availableLanguages')}</h2>
        <div className="flex justify-center space-x-12">
          <Link to={`/${interfaceLang}/courses/es`} className="flex flex-col items-center hover:opacity-80 transition">
            <img src={esFlag} alt="Español" className="w-24 h-24 object-cover rounded-full shadow-md" />
            <span className="mt-2 text-lg font-semibold">{t('spanish')}</span>
          </Link>
          <Link to={`/${interfaceLang}/courses/en`} className="flex flex-col items-center hover:opacity-80 transition">
            <img src={gbFlag} alt="English" className="w-24 h-24 object-cover rounded-full shadow-md" />
            <span className="mt-2 text-lg font-semibold">{t('english')}</span>
          </Link>
        </div>
      </section>

      {/* LEARNING TECHNIQUES SECTION */}
      <section className="py-16 bg-gray-100 text-center" style={{ backgroundImage: `url(${carteLangueImage})` }}>
        <h2 className="text-3xl text-white text-shadow font-bold mb-6">{t('learningTechniques')}</h2>
        <p className="text-lg text-white text-shadow max-w-3xl mx-auto mb-12">{t('learningDescription')}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          <div className="p-6 bg-white rounded-2xl shadow-lg">
            <h3 className="text-2xl font-semibold mb-4">{t('courses')}</h3>
            <img src={lessonImage} alt="leçon" className="object-contain" />
            <p>{t('coursesDescription')}</p>
          </div>
          <div className="p-6 bg-white rounded-2xl shadow-lg">
            <h3 className="text-2xl font-semibold mb-4">{t('exercises')}</h3>
            <img src={exerciceLangueImage} alt="leçon" className="object-contain" />
            <p>{t('exercisesDescription')}</p>
          </div>
          <div className="p-6 bg-white rounded-2xl shadow-lg">
            <h3 className="text-2xl font-semibold mb-4">{t('levelTests')}</h3>
            <img src={ficheMemoImage} alt="leçon" className="object-contain" />
            <p>{t('levelTestsDescription')}</p>
          </div>
          <div className="p-6 bg-white rounded-2xl shadow-lg">
            <h3 className="text-2xl font-semibold mb-4">{t('revisionSheets')}</h3>
            <img src={quizzImage} alt="leçon" className="object-contain" />
            <p>{t('revisionSheetsDescription')}</p>
          </div>
        </div>
      </section>

      {/* TEST NOTIFICATION BUTTON */}
      <section className="py-8 text-center">
        <button
          onClick={handleTestNotification}
          className="bg-indigo-500 text-white px-5 py-3 rounded-xl hover:bg-indigo-600 transition"
        >
          {t('sendTestNotification')}
        </button>
      </section>
    </div>
  );
}

export default Home;
